import { EditorView, KeyBinding } from "@codemirror/view";

/**
 * Regex to detect list markers.
 * Captures: [1: indent] [2: bullet or number+dot] [3: optional checkbox with trailing space]
 */
const LIST_MARKER_RE = /^(\s*)([-*]|\d+\.)\s(\[[ x]\]\s)?/;

/**
 * Parse a list marker from a line of text.
 * Returns null if the line is not a list item.
 */
function parseListMarker(lineText: string) {
  const match = lineText.match(LIST_MARKER_RE);
  if (!match) return null;

  const indent = match[1];
  const marker = match[2];
  const checkbox = match[3] || "";
  const fullMarker = match[0]; // entire matched prefix including trailing space

  return { indent, marker, checkbox, fullMarker };
}

/**
 * Build the next marker for a new list item after Enter.
 * - Bullets stay the same (`- ` or `* `)
 * - Numbers increment (`2.` -> `3.`)
 * - Checkboxes become unchecked (`- [ ] `)
 */
function nextMarker(parsed: { indent: string; marker: string; checkbox: string }): string {
  let newMarker: string;

  if (/^\d+\.$/.test(parsed.marker)) {
    const num = parseInt(parsed.marker, 10);
    newMarker = `${num + 1}.`;
  } else {
    newMarker = parsed.marker;
  }

  const checkboxPart = parsed.checkbox ? "[ ] " : "";
  return `${parsed.indent}${newMarker} ${checkboxPart}`;
}

/**
 * Renumber all adjacent numbered list items at the same indentation level,
 * starting from a given line. Walks up to find the first item at this indent,
 * walks down to find the last, then renumbers all of them starting from 1.
 */
function renumberList(view: EditorView, lineNumber: number): void {
  const doc = view.state.doc;
  const refLine = doc.line(lineNumber);
  const refParsed = parseListMarker(refLine.text);

  // Only renumber numbered lists
  if (!refParsed || !/^\d+\.$/.test(refParsed.marker)) return;

  const indentLevel = refParsed.indent;

  // Walk up to find the first numbered item at this indent level
  let firstLine = lineNumber;
  for (let i = lineNumber - 1; i >= 1; i--) {
    const line = doc.line(i);
    const parsed = parseListMarker(line.text);
    if (!parsed) break;
    // If same indent and numbered, extend range
    if (parsed.indent === indentLevel && /^\d+\.$/.test(parsed.marker)) {
      firstLine = i;
    } else if (parsed.indent.length < indentLevel.length) {
      // Hit a parent level — stop
      break;
    }
    // If deeper indent, skip (it's a sub-item) and keep walking
  }

  // Walk down to find the last numbered item at this indent level
  let lastLine = lineNumber;
  const totalLines = doc.lines;
  for (let i = lineNumber + 1; i <= totalLines; i++) {
    const line = doc.line(i);
    const parsed = parseListMarker(line.text);
    if (!parsed) break;
    if (parsed.indent === indentLevel && /^\d+\.$/.test(parsed.marker)) {
      lastLine = i;
    } else if (parsed.indent.length < indentLevel.length) {
      break;
    }
  }

  // Build changes to renumber
  const changes: { from: number; to: number; insert: string }[] = [];
  let counter = 1;
  for (let i = firstLine; i <= lastLine; i++) {
    const line = doc.line(i);
    const parsed = parseListMarker(line.text);
    if (!parsed) continue;
    if (parsed.indent !== indentLevel) continue;
    if (!/^\d+\.$/.test(parsed.marker)) continue;

    const expectedMarker = `${counter}.`;
    if (parsed.marker !== expectedMarker) {
      // Replace just the number portion: indent + old marker -> indent + new marker
      const markerStart = line.from + parsed.indent.length;
      const markerEnd = markerStart + parsed.marker.length;
      changes.push({ from: markerStart, to: markerEnd, insert: expectedMarker });
    }
    counter++;
  }

  if (changes.length > 0) {
    view.dispatch({ changes });
  }
}

/**
 * Enter key handler for list continuation.
 */
function handleEnter(view: EditorView): boolean {
  const { state } = view;
  const { main } = state.selection;

  // Only handle when there's no selection range
  if (main.from !== main.to) return false;

  const line = state.doc.lineAt(main.head);
  const parsed = parseListMarker(line.text);
  if (!parsed) return false;

  const contentAfterMarker = line.text.slice(parsed.fullMarker.length);

  if (contentAfterMarker.trim() === "") {
    // Empty list item — remove the marker
    if (parsed.indent.length >= 2) {
      // Outdent: remove 2 spaces of indentation, keep the marker
      const newIndent = parsed.indent.slice(2);
      const checkboxPart = parsed.checkbox || "";
      const newLine = `${newIndent}${parsed.marker} ${checkboxPart}`;
      view.dispatch({
        changes: { from: line.from, to: line.to, insert: newLine },
        selection: { anchor: line.from + newLine.length },
      });
      // Renumber at the new indent level
      const newLineNumber = view.state.doc.lineAt(line.from).number;
      renumberList(view, newLineNumber);
    } else {
      // Top level — remove the entire marker, leaving empty line
      view.dispatch({
        changes: { from: line.from, to: line.to, insert: "" },
        selection: { anchor: line.from },
      });
    }
    return true;
  }

  // Line has content — insert new list item
  const newMarkerPrefix = nextMarker(parsed);
  const insertText = "\n" + newMarkerPrefix;

  view.dispatch({
    changes: { from: main.head, insert: insertText },
    selection: { anchor: main.head + insertText.length },
  });

  // For numbered lists, renumber from the new line onward
  if (/^\d+\.$/.test(parsed.marker)) {
    // The new line is the one after current; get its line number
    const newPos = main.head + insertText.length;
    const newLineNumber = view.state.doc.lineAt(newPos).number;
    renumberList(view, newLineNumber);
  }

  return true;
}

/**
 * Tab handler for indenting list items.
 */
function handleTab(view: EditorView): boolean {
  const { state } = view;
  const { main } = state.selection;
  const line = state.doc.lineAt(main.head);
  const parsed = parseListMarker(line.text);
  if (!parsed) return false;

  // Add 2 spaces of indentation at line start
  view.dispatch({
    changes: { from: line.from, insert: "  " },
    selection: { anchor: main.head + 2 },
  });

  // Renumber at old indent level (parent) and new indent level (child)
  if (/^\d+\.$/.test(parsed.marker)) {
    const newLineNumber = view.state.doc.lineAt(line.from).number;
    renumberList(view, newLineNumber);
    // Also renumber the previous indent level (parent items shifted)
    if (newLineNumber > 1) {
      renumberList(view, newLineNumber - 1);
    }
  }

  return true;
}

/**
 * Shift+Tab handler for outdenting list items.
 */
function handleShiftTab(view: EditorView): boolean {
  const { state } = view;
  const { main } = state.selection;
  const line = state.doc.lineAt(main.head);
  const parsed = parseListMarker(line.text);
  if (!parsed) return false;

  // No indentation to remove
  if (parsed.indent.length === 0) return false;

  // Remove up to 2 spaces from line start
  const spacesToRemove = Math.min(2, parsed.indent.length);
  view.dispatch({
    changes: { from: line.from, to: line.from + spacesToRemove },
    selection: { anchor: Math.max(line.from, main.head - spacesToRemove) },
  });

  // Renumber affected levels
  if (/^\d+\.$/.test(parsed.marker)) {
    const newLineNumber = view.state.doc.lineAt(line.from).number;
    renumberList(view, newLineNumber);
    // Also renumber the deeper level we just left
    if (newLineNumber < view.state.doc.lines) {
      renumberList(view, newLineNumber + 1);
    }
  }

  return true;
}

/**
 * CM6 keymap for list editing behavior.
 * Intercepts Enter, Tab, and Shift+Tab inside list items.
 */
export const listEditingKeymap: KeyBinding[] = [
  { key: "Enter", run: handleEnter },
  { key: "Tab", run: handleTab },
  { key: "Shift-Tab", run: handleShiftTab },
];
